// Fixed "Diagnostics ON" indicator with Export Trace / End Diagnostics.
// Renders ONLY when diagnostic mode is active (?diag=1). Zero footprint otherwise.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, Download, X, Loader2 } from 'lucide-react';
import { isDiagOn, getSession, initDiagnostics, exportTrace, endDiagnostics } from '../lib/diagnostics';

const API = process.env.REACT_APP_BACKEND_URL;

export const DiagnosticBadge = () => {
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { initDiagnostics(); setOn(isDiagOn()); }, []);
  if (!on) return null;

  const handleExport = async () => {
    setBusy(true);
    try {
      // trigger the read-only score/provenance snapshot (pinned jobs come from sessionStorage)
      const pinned = JSON.parse(sessionStorage.getItem('st_diag_pinned') || '[]');
      try {
        await axios.post(`${API}/api/diag/snapshot`,
          { session_id: getSession(), pinned_job_ids: pinned },
          { withCredentials: true, timeout: 30000, headers: { 'X-ST-Diagnostic-Session': getSession() } });
      } catch { /* snapshot best-effort; export still proceeds */ }
      await exportTrace();
    } finally { setBusy(false); }
  };

  const handleEnd = () => { endDiagnostics(); setOn(false); };

  return (
    <div data-testid="diagnostics-badge"
      style={{ position: 'fixed', bottom: 12, left: 12, zIndex: 99999 }}
      className="flex items-center gap-2 rounded-full bg-amber-500 text-black shadow-lg px-3 py-1.5 text-xs font-semibold">
      <Activity className="w-4 h-4 animate-pulse" />
      <span data-testid="diagnostics-on-label">Diagnostics ON</span>
      <button data-testid="diag-export-btn" onClick={handleExport} disabled={busy}
        className="ml-1 flex items-center gap-1 rounded-full bg-black/80 text-amber-300 px-2 py-0.5 hover:bg-black">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Export Trace
      </button>
      <button data-testid="diag-end-btn" onClick={handleEnd}
        className="flex items-center gap-1 rounded-full bg-black/80 text-amber-300 px-2 py-0.5 hover:bg-black">
        <X className="w-3 h-3" /> End
      </button>
    </div>
  );
};

// Diagnostic-only "Add to Trace" pin for a job card. No-op/invisible for normal users.
// Reuses the EXISTING sessionStorage 'st_diag_pinned' max-10 store consumed by the Export snapshot.
export const PinJobButton = ({ jobId }) => {
  const [on] = useState(isDiagOn());
  const readPinned = () => {
    try { return JSON.parse(sessionStorage.getItem('st_diag_pinned') || '[]'); } catch { return []; }
  };
  const [pinned, setPinned] = useState(() => readPinned().includes(jobId));
  if (!on || !jobId) return null;
  const toggle = (e) => {
    if (e) e.stopPropagation();
    try {
      let cur = readPinned();
      if (cur.includes(jobId)) {
        cur = cur.filter((id) => id !== jobId);
        setPinned(false);
      } else {
        cur.push(jobId);
        cur = cur.slice(-10);
        setPinned(cur.includes(jobId));
      }
      sessionStorage.setItem('st_diag_pinned', JSON.stringify(cur));
    } catch {}
  };
  return (
    <button data-testid={`pin-job-${jobId}`} onClick={toggle}
      className={`text-[10px] px-1.5 py-0.5 rounded border ${pinned
        ? 'bg-amber-500 text-black border-amber-600'
        : 'bg-amber-200 text-amber-900 border-amber-400'}`}>
      {pinned ? 'Pinned' : 'Add to Trace'}
    </button>
  );
};
